import React, { useState, useCallback } from 'react';
// import { makeStyles } from 'design-system/styles';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tooltip from '@material-ui/core/Tooltip';
import { AddSection } from './AddSection';

import { useSlate } from 'slate-react';
// eslint-disable-next-line restrict-imports/restrict-imports
import { makeStyles } from 'design-system/product/theme';
import { Button, CircularProgress } from 'design-system';
import { Checkbox } from 'react-scale/components/design-system';
import { useToggle } from 'react-use-v13';
import moment from 'moment';
import DescriptionModal from 'react-scale/dashboard.scale.com/components/SelfServe/Instructions/DescriptionModal';

const useStyles = makeStyles(theme => ({
  toolbar: {
    background: '#F8F8F8',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing(0, 2),
    minWidth: 600,
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  activeIcon: {
    color: `${theme.colors.primary1} !important`,
  },
  icon: {
    width: 18,
    height: 18,
  },
  text: {
    fontSize: '16px',
    fontWeight: 'normal',
  },
  actions: { display: 'flex', marginLeft: 'auto', justifyContent: 'end', alignItems: 'center' },
  updateActions: {
    width: '300px',
    display: 'flex',
    marginRight: theme.spacing(2),
    marginLeft: theme.spacing(2),
  },
}));

interface EditorToolbarProps {
  toggleMark: Function;
  toggleBlock: Function;
  isBlockActive: Function;
  isMarkActive: Function;
  lastSelection: any;
  shouldShowLabeledExamples: boolean;
  handleSave?: Function;
  updatedAt?: string;
  isDirty?: boolean;
  isOnboardingComplete?: boolean;
}
const EditorToolbar: React.FC<EditorToolbarProps> = ({
  toggleMark,
  toggleBlock,
  isBlockActive,
  isMarkActive,
  lastSelection,
  shouldShowLabeledExamples = true,
  handleSave,
  updatedAt,
  isDirty = true,
  isOnboardingComplete = true,
}) => {
  const classes = useStyles();
  const editor = useSlate();
  const [updateTasks, toggleUpdateTasks] = useToggle(false);
  const [updateLabelers, toggleUpdateLabelers] = useToggle(false);
  const [showDescriptionInput, setShowDescriptionInput] = useToggle(false);
  const [changeDescription, setChangeDescription] = useState('');
  const [saving, setSaving] = useToggle(false);
  const handleToggleUpdateLabelers = useCallback(() => {
    if (!updateLabelers) {
      setShowDescriptionInput(true);
    }
    toggleUpdateLabelers();
  }, [updateLabelers, toggleUpdateLabelers, setShowDescriptionInput]);

  const handleMouseDown = (
    event: React.MouseEvent<HTMLElement>,
    format: string,
    level = 'mark',
  ) => {
    event.preventDefault();
    if (level === 'mark') toggleMark(editor, format);
    else if (level === 'block') toggleBlock(editor, format);
  };

  return (
    <Toolbar className={classes.toolbar}>
      {/* block level formatting options: */}
      {[1, 2, 3, 4].map((_, index) => {
        return (
          <Tooltip key={index} placement="bottom" title={`Header Level ${index + 1}`}>
            <IconButton
              edge="start"
              color="inherit"
              onMouseDown={event => handleMouseDown(event, `heading-${index + 1}`, 'block')}
            >
              <span
                className={`${classes.text} ${
                  isBlockActive(editor, `heading-${index + 1}`) ? classes.activeIcon : ''
                } `}
              >
                {`H${index + 1}`}
              </span>
            </IconButton>
          </Tooltip>
        );
      })}

      {/* mark (text) level formatting options: */}
      <Tooltip placement="bottom" title="Bold">
        <IconButton
          edge="start"
          color="inherit"
          onMouseDown={event => handleMouseDown(event, 'bold')}
        >
          <FontAwesomeIcon
            icon="bold"
            className={`${classes.icon} ${isMarkActive(editor, 'bold') ? classes.activeIcon : ''}`}
          />
        </IconButton>
      </Tooltip>
      <Tooltip placement="bottom" title="Italic">
        <IconButton
          edge="start"
          color="inherit"
          onMouseDown={event => handleMouseDown(event, 'italic')}
        >
          <FontAwesomeIcon
            icon="italic"
            className={`${classes.icon} ${
              isMarkActive(editor, 'italic') ? classes.activeIcon : ''
            } `}
          />
        </IconButton>
      </Tooltip>
      <Tooltip placement="bottom" title="Underline">
        <IconButton
          edge="start"
          color="inherit"
          onMouseDown={event => handleMouseDown(event, 'underline')}
        >
          <FontAwesomeIcon
            icon="underline"
            className={`${classes.icon} ${
              isMarkActive(editor, 'underline') ? classes.activeIcon : ''
            } `}
          />
        </IconButton>
      </Tooltip>

      {/* more block level formatting options: */}
      <Tooltip placement="bottom" title="Bulleted List">
        <IconButton
          edge="start"
          color="inherit"
          onMouseDown={event => handleMouseDown(event, 'bulleted-list', 'block')}
        >
          <FontAwesomeIcon
            icon="list"
            className={`${classes.icon} ${
              isBlockActive(editor, 'bulleted-list') ? classes.activeIcon : ''
            } `}
          />
        </IconButton>
      </Tooltip>
      <Tooltip placement="bottom" title="Numbered List">
        <IconButton
          edge="start"
          color="inherit"
          onMouseDown={event => handleMouseDown(event, 'numbered-list', 'block')}
        >
          <FontAwesomeIcon
            icon="list-ol"
            className={`${classes.icon} ${
              isBlockActive(editor, 'numbered-list') ? classes.activeIcon : ''
            } `}
          />
        </IconButton>
      </Tooltip>

      <AddSection
        lastSelection={lastSelection}
        shouldShowLabeledExamples={shouldShowLabeledExamples}
      />

      {updatedAt && (
        <div style={{ marginLeft: 8 }}>Updated: {moment(updatedAt).format('MM/DD/YY hh:mmA')}</div>
      )}
      <>
        {handleSave && (
          <div className={classes.actions}>
            {isOnboardingComplete && (
              <div className={classes.updateActions}>
                {showDescriptionInput && (
                  <DescriptionModal
                    modalOpen={showDescriptionInput}
                    setModalOpen={setShowDescriptionInput}
                    changeDescription={changeDescription}
                    setChangeDescription={setChangeDescription}
                  />
                )}
                <Checkbox
                  label="update labelers"
                  checked={updateLabelers}
                  onChange={handleToggleUpdateLabelers}
                />
                <Tooltip title="Force labelers to reread the new instructions." placement="top">
                  <div style={{ paddingLeft: '4px', lineHeight: '32px', textAlign: 'center' }}>
                    <FontAwesomeIcon icon="question-circle" color="gray" />
                  </div>
                </Tooltip>
                <Checkbox label="update tasks" checked={updateTasks} onChange={toggleUpdateTasks} />
                <Tooltip
                  title="Update existing pending tasks with new instructions."
                  placement="top"
                >
                  <div style={{ paddingLeft: '4px', lineHeight: '32px', textAlign: 'center' }}>
                    <FontAwesomeIcon icon="question-circle" color="gray" />
                  </div>
                </Tooltip>
              </div>
            )}
            <Button
              type="primary"
              disabled={!handleSave || !isDirty || saving}
              onClick={async () => {
                if (handleSave) {
                  setSaving(true);
                  await handleSave(updateTasks, updateLabelers, changeDescription);
                  setChangeDescription('');
                  setSaving(false);
                }
              }}
            >
              Save
              {!!saving && <CircularProgress size={12} color="primary" style={{ marginLeft: 8 }} />}
            </Button>
          </div>
        )}
      </>
    </Toolbar>
  );
};

export default EditorToolbar;
